<template>
  <Header />
  <EmailConfirmationMessage />
  <Message class="mr-4 ml-4 mt-4" v-if="usingShare2">{{ $t('message_use_regular_domain') }} <a href="https://share.problind.org/">share.problind.org</a></Message>
  <main>
    <router-view />
  </main>
  <Footer :playerVisible="playerVisible" />
  <ConfirmDialog
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '50vw' }"
  />
  <LoginDialog :showToasts="true" ref="theLoginDialog" />
  <div
    v-if="globalProps?.accountInfo?.isAdmin || globalProps?.accountInfo?.isManager"
    id="cardPermissionHighlight"
  >
    <label id="highlightPermissionChecksLabel" class="mr-2">{{
      $t("checkbox_highlight_permission_checks")
    }}</label>
    <Checkbox
      aria-labelledby="highlightPermissionChecksLabel"
      v-model="globalProps.highlightPermissionChecks"
      :binary="true"
    />
  </div>
  <Toast
    position="bottom-center"
    group="bc"
    @close="event => onToastClosed((event as any).message)"
  />

  <div v-show="playerVisible" class="bottomOverlay">
    <Player ref="thePlayer" :enableOpus2Caf="true" />
  </div>
</template>

<script lang="ts">
import { EmailConfirmationMessage } from "@/components";
import Footer from "@/views/Footer.vue";
import Header from "@/views/Header.vue";
import { lang, Language } from "@tactonom/lang-js";
import { LoginDialog, PermissionCheck, Player } from "editor-base/components";
import {
  PlayerInterface,
  UserRole,
  GraphicMetadata as ItemMetadata
} from "editor-base/utils/OtherTypes";
import { templateToast, parseMetadata, truncateEnd, onToastClosed } from "editor-base/utils/Tools";
import i18next from "i18next";
import { type GlobalProps } from "lib/editor-base/utils/GlobalProps";
import Checkbox from "primevue/checkbox";
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import { defineComponent } from "vue";
import { compress, decompress} from "lz-string";
import Message from "primevue/message";

interface AppData {
  intervals: Array<number>;

  // The globalProps are accessible to any Vue component, but to make them visible in
  // the Vue development tools while debugging, they have to be re-exported somewhere.
  globalPropsAccessor: GlobalProps | undefined;
  UserRole: typeof UserRole;
  loadingItems: Record<ItemKind, boolean>;
  graphics: Array<ItemMetadata>;
  collections: Array<ItemMetadata>;
  graphicsById: Record<string, ItemMetadata>,
  collectionsById: Record<string, ItemMetadata>,
  onToastClosed: typeof onToastClosed,
}

type ItemKind = "graphic" | "collection";

export default defineComponent({
  name: "App",
  components: {
    Header,
    Footer,
    EmailConfirmationMessage,
    PermissionCheck,
    Checkbox,
    ConfirmDialog,
    Toast,
    LoginDialog,
    Player,
    Message,
  },
  data(): AppData {
    return {
      intervals: [],
      globalPropsAccessor: undefined,
      UserRole,
      loadingItems: {
        graphic: false,
        collection: false,
      },
      graphics: [],
      collections: [],
      graphicsById: {},
      collectionsById: {},
      onToastClosed,
    };
  },
  provide() {
    return {
      loadingItems: this.loadingItems,
      graphics: this.graphics,
      collections: this.collections,
    };
  },
  async mounted() {
    // TODO full remove addLinkListener
    //this.addLinkListener();
    this.loadItemsFromLocalStorage("graphic");
    this.loadItemsFromLocalStorage("collection");
    await this.updateAccountData();
    await this.updateTagData();

    this.intervals.push(window.setInterval(this.updateAccountData               ,      30 * 1000)); // 30 sec
    this.intervals.push(window.setInterval(this.updateTagData                   ,  5 * 60 * 1000)); //  5 minutes

    // item data is fully updated on page load, and the first few items whenever the user
    // navigates to the item browser. In addition, perform partial and full updates regularly:
    this.intervals.push(window.setInterval(() => this.loadItemsFromServer(undefined, true) ,  5 * 60 * 1000)); //  5 minutes, update only first few items
    this.intervals.push(window.setInterval(() => this.loadItemsFromServer(undefined, false), 45 * 60 * 1000)); // 45 minutes, update all items

    this.globalPropsAccessor = this.globalProps;
    window.globalPropsAccessor = this.globalProps;
    this.globalProps.triggerLogin = async (loginReason?: string) => {
      return this.$refs.theLoginDialog.show(loginReason);
    };
    this.globalProps.triggerLogout = async () => {
      // copied from ProfileRegister.vue
      // TODO unify login and logout, we already have a feature branch for that
      this.loading = true;
      const result = await fetch(import.meta.env.VITE_SVG_REPO_BASE_URL + "/ctrl/auth/page/logout", {
        method: "GET",
        credentials: "include",
      });
      this.loading = false;

      if (result.ok || result.redirected) {
        templateToast("logout", "success");
        this.globalProps.accountInfo.id = undefined;
        this.globalProps.accountInfo.isLoggedIn = false;
        this.globalProps.accountInfo.isAdmin = false;
        this.globalProps.accountInfo.isManager = false;
      } else {
        templateToast("logout", "error");
        // We don't know if we're still logged in, but this should trigger an update of the accountInfo anyway:
        this.globalProps.accountInfo.isLoggedIn = undefined;
      }
      // TODO if a document is loaded, its metadata must be reloaded, so that
      // the new user rights apply
    };
    this.loadItemsFromServer();
    this.globalProps.player = this.$refs.thePlayer as PlayerInterface;
  },
  unmounted() {
    for(let interval of this.intervals) {
      window.clearInterval(interval);
    }
    this.intervals = [];
  },
  watch: {
    "globalProps.accountInfo.id": {
      async handler(newId: string | undefined, oldId: string | undefined) {
        // This code shall only be called if changing from one login directly to another.
        // Transitions between login and logout are handled by the other watcher.
        if (oldId && newId && (oldId != newId)) {
          let response = await fetch("/ctrl/auth/api/account-info/me");
          this.globalProps.accountInfo = await response.json();
          this.loadItemsFromServer();
        }
      }
    },
    "globalProps.accountInfo.isLoggedIn": {
      async handler(newIsLoggedIn: boolean, oldIsLoggedIn: boolean) {
        // comparing negated booleans is needed to ignore for example a change from null to false.
        if (!oldIsLoggedIn != !newIsLoggedIn) {
          let response = await fetch("/ctrl/auth/api/account-info/me");
          this.globalProps.accountInfo = await response.json();
          this.loadItemsFromServer();
        }
      }
    },
  },
  methods: {
    /// Switches between German and English. Will no longer make sende when we support more then two UI languages.
    switchUiLanguage(): void {
      const currentLanguage = this.globalProps.uiLang;
      const isCurrentEnglish = Language.equal(lang.getEnglish(), currentLanguage);
      const newLang = (isCurrentEnglish ? lang.getLanguageByTag("de") : lang.getLanguageByTag("en")) || lang.getDefaultLanguage();
      console.log("User requested switch from ", currentLanguage.toString(), " to ", newLang.toString());
      this.setUiLanguage(newLang);
    },
    /// Sets the new UI language and saves it to the cookie and localStorage.
    setUiLanguage(newLang: Language): void {
      document.cookie = "uilanguage=" + newLang.toString() + "; Path=/";
      window.localStorage.setItem("uilanguage", newLang.toString());
      i18next.changeLanguage(newLang.toString());
      this.globalProps.uiLang = newLang;
    },
    addLinkListener() {
      // taken from https://dennisreimann.de/articles/delegating-html-links-to-vue-router.html and adjusted for Typescript
      window.addEventListener('click', event => {
        // ensure we use the link, in case the click has been received by a subelement
        let { target } = event;
        while (target && target instanceof Element && target.tagName.toLowerCase() != 'a') target = target.parentNode;

        // handle only links
        if (target && target instanceof Element && target instanceof HTMLAnchorElement && target.href) {
          const url = new URL(target.href);
          const to = url.pathname;

          // don't handle external links
          if (target.matches("a[href*='://']")) {
            return;
          }

          // some sanity checks taken from vue-router:
          // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
          const { altKey, ctrlKey, metaKey, shiftKey, button, defaultPrevented } = event;

          // don't handle with control keys
          if (metaKey || altKey || ctrlKey || shiftKey) return;

          // don't handle when preventDefault called
          if (defaultPrevented) return;

          // don't handle right clicks
          if (button !== undefined && button !== 0) return;

          // don't handle if `target="_blank"`
          if (target && target.getAttribute) {
            const linkTarget = target.getAttribute('target') || ""
            if (/\b_blank\b/i.test(linkTarget)) return;
          }

          // handle language switch
          if (url.searchParams.has("uilanguage")) {
            // here we completely ignore the rest of the URL, because we assume this comes from the link in the upper right,
            // which has not been updated along with changes by vue router. No matter what this old URL says, we want to
            // stay on the same page, which is done with a simple reload.
            event.preventDefault();

            const currentLanguage = this.globalProps.uiLang;
            const newLang = lang.getLanguageByTag(url.searchParams.get("uilanguage") || undefined);
            console.log("User requested switch from ", currentLanguage, " to ", newLang);
            // this should fix #807 and indirectly #702. It's not the full solution though,
            const ignoreCurrentLanguage = true;
            if (ignoreCurrentLanguage || !Language.equal(newLang, currentLanguage)) {
              console.log("Handle language switch to " + newLang?.display(lang.getEnglish()) + ". window.location is " + window.location.toString());
              document.cookie = "uilanguage=" + (newLang?.toString() || "") + "; Path=/";
              window.localStorage.setItem("uilanguage", newLang?.toString() || "");

              if (this.globalProps.editor?.document?.changed) {
                // special case: if the graphic has unsaved changes, apply the language change without reload:
                // i18next.changeLanguage(newLang || undefined);

                // But it does not work yet, because the vue components do not know that they should re-render now.
                // So do a reload instead:
                window.location.reload();
              } else {
                window.location.reload();
              }
            }
            return;
          }

          if (window.location.pathname == to) {
            // this is a same-page link, might contain an anchor or not.
            // the script from dennisreimann.de would not handle those, but
            // I think we should handle them, or we get full page reloads
            // i.e. when the user already is on the list und clicks on "Browse".

            if(url.hash) {
              console.log("Scroll to anchor via normal vue-router handling.");

              // We could also do this, but then we would also need to push a history state
              // manually, which will be handled by vue-router instead.
              // document.querySelector(url.hash)?.scrollIntoView({
              //   behavior: 'smooth'
              // });
              // event.preventDefault();
            } else {
              console.log("Navigation to same url, but no hash given. Aborting routing, letting native behavior happen.");
              return;
            }

            // TODO we do not handle query params (except `uilanguage`) here
            // TODO we need to handle them if a graphic show page should link directly to another such page,
            // like when navigating clones.
          }

          if (event.preventDefault) {
            const resolved = this.$router.resolve(to);
            if (resolved.matched.length && resolved.matched[0]!.name != "catchAll") {
              event.preventDefault();

              const query: Record<string, string> = {};
              url.searchParams.forEach((value, key) => { query[key] = value; });

              this.$router.push({ path: to, query, hash: url.hash });
            } else {
              console.log("Link to " + to + " does not resolve, doing nothing.");
            }
          }
        }
      });
    },
    async updateAccountData() {
      try {
        let response = await fetch("/ctrl/auth/api/account-info/me");
        this.globalProps.accountInfo = await response.json();
      } catch (e) {
        console.log("Error during load of account-info: ", e);
      }
    },
    async updateTagData() {
      try {
        let response = await fetch("/ctrl/tag/api/list");
        this.globalProps.tags = await response.json();
      } catch (e) {
        console.log("Error during load of tag list: ", e);
      }
    },

    loadItemsFromLocalStorage(kind: ItemKind): void {
      let prefix = (kind == "graphic") ? "gm:" : "cm:";
      let items = (kind == "graphic") ? this.graphics : this.collections;
      let itemsById = (kind == "graphic") ? this.graphicsById : this.collectionsById;
      let errorCount = 0;
      let successCount = 0;
      if (localStorage) {
        let failedKeys: Array<string> = [];
        for (let i = 0; i < localStorage.length; i++) {
          let key = localStorage.key(i);
          try {
            if (key?.startsWith(prefix)) {
              let value = JSON.parse(decompress(localStorage.getItem(key) || "") || "{}");
              if (value.language) {
                value.language = lang.getLanguageByTag(value.language);
              }
              items.push(value);
              itemsById[value.id || value.key] = value;
              successCount++;
            }
          } catch(e) {
            failedKeys.push(key!);
            errorCount++;
          }
        }
        if (errorCount) {
          for (const key of failedKeys) {
            localStorage.removeItem(key);
          }
        }
      }
      console.log("Done loading " + kind + "s from local storage, " + successCount + " success and " + errorCount + " error.");
    },
    async loadItemsFromServer(kind: ItemKind, onlyFirstPage?: boolean): Promise<void> {
      
      if(!kind) {
        this.loadItemsFromServer("graphic", onlyFirstPage);
        this.loadItemsFromServer("collection", onlyFirstPage);
        return;
      } else {
        //console.log("Try to update " + kind + "s, " + (onlyFirstPage ? "but only fist page." : "all of them."));
      }

      let quotaReached = false;

      let localStorageReplacer = (key: string, value: any) => {
        if (key == "family" || key == "metrics") {
          return null;
        }
        if (key == "description") {
          return truncateEnd(value, 30);
        }
        return value;
      };

      // Try to load all items of the given kind in small batches - a new one is requested after
      // the previous one has arrived and is parsed.
      // TODO Handle errors and retry / resume
      try {
        if (this.loadingItems[kind]) {
          //console.warn("Already loading items of kind " + kind + ", ignoring additional call.");
          return;
        }
        this.loadingItems[kind] = true;

        let stepSize = 65;

        if (onlyFirstPage) {
          stepSize = 10;
        }

        let prefix = (kind == "graphic") ? "gm:" : "cm:";
        let items = (kind == "graphic") ? this.graphics : this.collections;
        let itemsById = (kind == "graphic") ? this.graphicsById : this.collectionsById;

        let startIndex = 0;

        let oldestLoadedItemModification = new Date();
        let now = new Date();

        while (true) {
          let endIndex = startIndex + stepSize;
          const response = await fetch(
            "/ctrl/" +
              kind +
              "/api/some?startIndex=" +
              startIndex +
              "&endIndex=" +
              endIndex +
              "&exactCount=false"
          );

          let added = 0;
          let updated = 0;
          //let processed = 0;

          // Both "416 Range Not Satisfiable" and "204 No Content" mark the end of the list
          if (response.status == 416 || response.status == 204) {
            // when all updates are processed, delete all items from the cache that
            // were not part of the update.
            oldestLoadedItemModification = new Date(0);
          } else {
            

            const itemsJsonSome = await response.json();
            let newItems = itemsJsonSome.map((item) => parseMetadata(item));
            //processed = newItems.length;
            for (const newItem of newItems) {
              const id = newItem.id || newItem.key;
              newItem.loadTime = now;
              if (!itemsById.hasOwnProperty(id)) {
                itemsById[id] = newItem;
                items.push(newItem);
                added++;
              } else {
                Object.assign(itemsById[id], newItem);
                if (!newItem.tags) {
                  // Object.assign does not remove properties, and if newItem has an empty tags array,
                  // that will have been removed by Micronaut during serialization. So we must
                  // explicitly apply the empty tags in case they were non-empty before.
                  itemsById[id].tags = [];
                }
                updated++;
              }

              if (!quotaReached) {
                try {
                  localStorage.setItem(prefix + id, compress(JSON.stringify(newItem, localStorageReplacer)));
                } catch(e) {
                  console.warn("Storage quota might be reached. Not storing any more items locally. Error: ", e);
                  quotaReached = true;
                }
              }

              let modificationDate = new Date(newItem.modificationTimestamp);
              if (modificationDate < oldestLoadedItemModification) {
                oldestLoadedItemModification = modificationDate;
              }
            }
          }

          // Now delete all items which were modified after oldestLoadedItemModification but not
          // loaded freshly from the server recently. Those items must have been deleted on the
          // server, or they would have been loaded by now.

          let removed = 0;
          let removedIds: Array<string> = [];

          for (const id of Object.keys(itemsById)) {
            let value = itemsById[id];
            let modificationDate = new Date(value.modificationTimestamp);
            if (modificationDate > oldestLoadedItemModification) {
              // check if this was loaded recently
              let loadTime = new Date(value.loadTime);
              if (loadTime < now) {
                delete itemsById[id];
                localStorage.removeItem(prefix + id);
                removedIds.push(id);
                removed++;
              }
            }
          }
          items.splice(0, items.length,...items.filter(item => !removedIds.includes(item.id || item.key)));
          // The line above filters items in-place, which is important to trigger computed
          // properties that depend on it. The line below results in the same contents, but breaks reactivity:
          //items = items.filter(item => !removedIds.includes(item.id || item.key));

          //console.log("Processed " + processed + " " + kind + "s from the server: " + added + " added, " + updated + " updated and " + removed + " removed.");
          //navigator.storage.estimate().then((estimate) => { console.log("Estimated size of local storage: " + estimate.usage?.toLocaleString() + " of " + estimate.quota?.toLocaleString() + " bytes." ) });

          if (response.status == 416 || response.status == 204) {
            //console.log("Reached end of " + kind + " update.");
            break;
          }

          if (onlyFirstPage) {
            this.loadingItems[kind] = false;
            return;
          }

          startIndex += stepSize - 2; // keep a tiny overlap between requests, in case a new graphic was added
          // to the database in between.

          // increase step size to limit the number of needed requests, but keeping the first requests quick
          stepSize += 15;
        }

        this.loadingItems[kind] = false;
      } catch (e) {
        this.loadingItems[kind] = false;
        console.log("Error during initial load of items and languages: ", e);
        templateToast("load_graphic_list", "error", { reason: e + "" });
      }
    },
  },
  computed: {
    playerVisible(): boolean {
      return this.$route.meta.needsPlayer;
    },
    usingShare2(): boolean {
      return window.location.host.startsWith("share2");
    },
  },
});
</script>
<style>
:root {
  --dark-purple: #781c50;
  --purple: #89155e;
}

html,
body {
  font-family: "Poppins", sans-serif;
  margin: 0;
  padding: 0;
  min-height: 100%;
}

main {
  min-width: 580px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 12px;
  padding-right: 12px;
  padding-bottom: 30px;
  min-height: 80vh;
  padding-top: 20px;
}

main.main-landing {
  max-width: 1700px;
  padding-left: 32px;
  padding-right: 32px;
}

main.main-browse {
  max-width: 1625px;
}

main.main-create {
  max-width: 900px;
}

main.main-upload {
  max-width: 1100px;
}

main.main-profile {
  max-width: 1100px;
}

main.main-reset_password_request {
  max-width: 700px;
}

main.main-reset_password_perform {
  max-width: 700px;
}

main.main-showGraphic {
  min-width: 720px;
}

main.main-showCollection {
  min-width: 1100px;
  max-width: 1500px;
}

main.main-admin {
  min-width: 800px;
  max-width: 1800px;
  padding-left: 32px;
  padding-right: 32px;
}

/* Has no effect in production environment, but simulates the Wordpress css when using `npm run dev`. */
#app {
  max-width: 1110px;
}

/* Better display for error messages with very long words, e.g. containing identifiers from code. */
.p-toast-message-text {
  word-break: break-word;
}

/* Prevents unnecessary word wraps in menus. */
.p-menu {
  width: unset !important;
}

/* Override theme color #383b53 (dark grey) with #bb0000 (red) */
.p-float-label > .p-invalid + label {
  color: #bb0000 !important;
}

.p-autocomplete.p-invalid.p-component > .p-inputtext,
.p-calendar.p-invalid.p-component > .p-inputtext,
.p-cascadeselect.p-invalid.p-component,
.p-checkbox.p-invalid > .p-checkbox-box,
.p-chips.p-invalid.p-component > .p-inputtext,
.p-dropdown.p-invalid.p-component,
.p-inputnumber.p-invalid.p-component > .p-inputtext,
.p-inputswitch.p-invalid .p-inputswitch-slider,
.p-inputtext.p-invalid.p-component,
.p-listbox.p-invalid,
.p-multiselect.p-invalid.p-component,
.p-password.p-invalid.p-component > .p-inputtext,
.p-radiobutton.p-invalid > .p-radiobutton-box,
.p-selectbutton.p-invalid > .p-button,
.p-treeselect.p-invalid.p-component,
.p-togglebutton.p-button.p-invalid > .p-button {
  border-color: #bb0000 !important;
}

.validationError {
  color: #bb0000;
}

/* Fix scrolling issue https://gitea.inventivio.com/Reader/svg-repo/issues/573#issuecomment-5992 
   * for native scrolling (may happen if a URL with anchor hash is explicitly (re-)loaded.
   * Navigation via vue-router needs a separate fix.
   */
#unlisted-results,
.anchor {
  scroll-margin-top: 110px;
}

.needsAdmin {
  filter: drop-shadow(0px 0px 3px #a24673);
}

.needsManager {
  filter: drop-shadow(0px 0px 3px #f49f52) drop-shadow(1px 1px 0px #f49f52);
}

.needsLoggedIn {
  filter: drop-shadow(0px 0px 3px #46a273);
}

#cardPermissionHighlight {
  padding: 1em;
  display: block flex;
  position: fixed !important;
  bottom: -3em;
  transition: bottom 200ms, background-color 200ms, border-color 200ms;
  left: -2px;
  height: 60px;
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  z-index: 1500;
}

#cardPermissionHighlight:hover {
  bottom: -2px;
  background: white;
  border-color: black;
}

.bottomOverlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1200;
  border-top: 2px solid var(--purple);
}


a.authorLink, a.upLink,
a.authorLink:visited, a.upLink:visited,
a.authorLink:active, a.upLink:active {
  text-decoration: none;
  color: black;
}

a.authorLink:hover, a.upLink:hover,
a.authorLink:focus, a.upLink:focus {
  text-decoration: underline;
  color: var(--purple);
  border-bottom: 3px solid transparent;
}

a.authorLink::after, a.upLink::after {
  content: " ";
  display: inline-block;
  border-bottom: 2px solid black;
  border-right: 2px solid black;
  height: 7px;
  width: 7px;
}

a.authorLink::after {
  transform: translateX(5px) rotate(-45deg);
}

a.upLink::after {
  transform: translateX(15px) rotate(-135deg);
  margin-right: 2em;
}

a.authorLink:hover::after, a.upLink:hover::after,
a.authorLink:focus::after, a.upLink:focus::after {
  border-color: var(--purple);
}
</style>
